import React, { useEffect, useMemo, useState } from 'react';
import { useTheme } from '../hooks/useTheme';
import { useNavigate } from 'react-router-dom';
import APIKeysManager from '../components/auth/APIKeysManager';
import { useAuth } from '../context/AuthContext';
import { useBackendStatus } from '../hooks/useBackendApi';
import { getLLMSettings, resetLLMSettings, saveLLMSettings } from '../api/llmSettingsApi';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LogoutIcon from '@mui/icons-material/Logout';

const DEFAULT_DEEPSEEK_SYSTEM_PROMPT = '\'Проанализируй следующую диаграмму и дай краткий анализ (не более 200 слов):\\n\\nКод диаграммы:\\n$'
  + '{diagramCode}\\n\\nСгенерированные элементы:\\n$'
  + '{elementsSummary}\\n\\nПожалуйста, дай анализ с точки зрения:\\n1. Что представляет данная диаграмма\\n2. Основные взаимодействия\\n3. Возможные улучшения или рекомендации\\n\\nОтвечай на русском языке.\'';

function getDefaultAnalysisPromptTemplate() {
  return DEFAULT_DEEPSEEK_SYSTEM_PROMPT;
}

const Profile = () => {
  const { theme } = useTheme();
  const [section, setSection] = useState('about');
  const navigate = useNavigate();
  const { user: authUser, logout } = useAuth();
  const { usingBackend } = useBackendStatus() ?? { usingBackend: false };
  const user = authUser
    ? { name: authUser.displayName || authUser.username, email: authUser.email, avatarUrl: null }
    : { name: 'DemoUser', email: 'demo@example.com', avatarUrl: null };

  const defaultPrompt = useMemo(() => getDefaultAnalysisPromptTemplate(), []);
  const [analysisPrompt, setAnalysisPrompt] = useState(defaultPrompt);
  const [checkRules, setCheckRules] = useState('');
  const [llmEnabled, setLlmEnabled] = useState(false);
  const [llmBaseUrl, setLlmBaseUrl] = useState('');
  const [llmModel, setLlmModel] = useState('');
  const [llmApiKey, setLlmApiKey] = useState('');
  const [llmTimeout, setLlmTimeout] = useState(30);
  const [llmHasApiKey, setLlmHasApiKey] = useState(false);
  const [llmLoading, setLlmLoading] = useState(false);
  const [llmSaving, setLlmSaving] = useState(false);
  const [llmMessage, setLlmMessage] = useState('');

  const isDark = theme.name === 'dark';
  const background = theme.background || (isDark ? '#0f1419' : '#f5f7fa');
  const surface = theme.codePanel.background || (isDark ? '#151b23' : '#ffffff');
  const surfaceSoft = isDark ? '#111827' : '#f8fafc';
  const border = theme.codePanel.border || (isDark ? 'rgba(148, 163, 184, 0.18)' : 'rgba(148, 163, 184, 0.28)');
  const text = theme.codePanel.text || (isDark ? '#e5e7eb' : '#111827');
  const muted = isDark ? '#94a3b8' : '#64748b';

  useEffect(() => {
    let mounted = true;
    setLlmLoading(true);
    getLLMSettings()
      .then((settings) => {
        if (!mounted) return;
        setLlmEnabled(Boolean(settings.configured && settings.enabled));
        setLlmBaseUrl(settings.base_url || '');
        setLlmModel(settings.model || '');
        setLlmTimeout(Number(settings.timeout_seconds) || 30);
        setLlmHasApiKey(Boolean(settings.has_api_key));
      })
      .catch((e) => {
        if (mounted) setLlmMessage(e.message || 'Не удалось загрузить настройки LLM');
      })
      .finally(() => {
        if (mounted) setLlmLoading(false);
      });
    return () => { mounted = false; };
  }, []);

  const handleLogout = async () => {
    if (logout) await logout();
    navigate('/login');
  };

  const handleSaveLLMSettings = async () => {
    setLlmSaving(true);
    setLlmMessage('');
    try {
      if (!llmEnabled) {
        await resetLLMSettings();
        setLlmBaseUrl('');
        setLlmModel('');
        setLlmApiKey('');
        setLlmTimeout(30);
        setLlmHasApiKey(false);
        setLlmMessage('Провайдер удален из списка моделей');
        return;
      }
      const payload = {
        enabled: true,
        base_url: llmBaseUrl.trim(),
        model: llmModel.trim(),
        timeout_seconds: clampTimeout(llmTimeout),
      };
      if (llmApiKey.trim()) {
        payload.api_key = llmApiKey.trim();
      }
      const saved = await saveLLMSettings(payload);
      setLlmBaseUrl(saved.base_url || '');
      setLlmModel(saved.model || '');
      setLlmTimeout(Number(saved.timeout_seconds) || payload.timeout_seconds);
      setLlmApiKey('');
      setLlmHasApiKey(Boolean(saved.has_api_key));
      setLlmMessage('Провайдер добавлен в список моделей');
    } catch (e) {
      setLlmMessage(e.message || 'Не удалось сохранить настройки LLM');
    } finally {
      setLlmSaving(false);
    }
  };

  const handleResetLLMSettings = async () => {
    setLlmSaving(true);
    setLlmMessage('');
    try {
      await resetLLMSettings();
      setLlmEnabled(false);
      setLlmBaseUrl('');
      setLlmModel('');
      setLlmApiKey('');
      setLlmTimeout(30);
      setLlmHasApiKey(false);
      setLlmMessage('Провайдер удален из списка моделей');
    } catch (e) {
      setLlmMessage(e.message || 'Не удалось сбросить настройки LLM');
    } finally {
      setLlmSaving(false);
    }
  };

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      background: surfaceSoft,
      color: text,
    },
    '& .MuiInputLabel-root': {
      color: muted,
    },
    '& .MuiFormHelperText-root': {
      color: muted,
    },
  };

  const cardSx = {
    border: `1px solid ${border}`,
    borderRadius: '8px',
    p: 2,
    background: surface,
    color: text,
  };

  return (
    <Box sx={{ minHeight: '100vh', background, color: text, display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 248px' } }}>
      <Box sx={{ p: { xs: 2, md: 3 }, minWidth: 0 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            gap: 2,
            pb: 2,
            borderBottom: `1px solid ${border}`,
            mb: 2,
            flexDirection: { xs: 'column', sm: 'row' },
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
            <Avatar
              src={user.avatarUrl || undefined}
              sx={{
                width: 56,
                height: 56,
                borderRadius: '8px',
                background: surfaceSoft,
                border: `1px solid ${border}`,
                color: text,
                fontWeight: 800,
              }}
            >
              {user.name.substring(0, 2).toUpperCase()}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 18, fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {user.name}
              </Typography>
              <Typography sx={{ fontSize: 13, color: muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {user.email}
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
              sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 800, borderColor: border, color: text }}
            >
              Назад
            </Button>
            <Button
              variant="outlined"
              startIcon={<LogoutIcon />}
              onClick={() => void handleLogout()}
              sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 800, borderColor: border, color: text }}
            >
              Выйти
            </Button>
          </Stack>
        </Box>

        {section === 'about' && (
          <Box sx={cardSx}>
            <Typography sx={{ fontSize: 16, fontWeight: 800, mb: 1 }}>О пользователе</Typography>
            <Typography sx={{ fontSize: 14, lineHeight: 1.6, color: muted }}>
              Профиль пользователя и персональные настройки платформы.
            </Typography>
          </Box>
        )}

        {section === 'api-keys' && <APIKeysManager />}

        {section === 'settings' && (
          <Stack spacing={2}>
            <Box sx={cardSx}>
              <Typography sx={{ fontSize: 14, fontWeight: 800, mb: 1 }}>Промпт для анализа диаграмм</Typography>
              <TextField
                value={analysisPrompt}
                onChange={(e) => setAnalysisPrompt(e.target.value)}
                multiline
                rows={8}
                fullWidth
                sx={fieldSx}
              />
              <Typography sx={{ fontSize: 12, color: muted, mt: 1 }}>
                Текст используется как шаблон анализа диаграмм.
              </Typography>
            </Box>

            <Box sx={cardSx}>
              <Typography sx={{ fontSize: 14, fontWeight: 800, mb: 1 }}>LLM-провайдер</Typography>
              <FormControlLabel
                control={<Checkbox checked={llmEnabled} onChange={(e) => setLlmEnabled(e.target.checked)} />}
                label="Добавить провайдер в список моделей"
                sx={{ color: text, mb: 1 }}
              />
              <Stack spacing={1.25}>
                <TextField
                  label="Base URL"
                  placeholder="http://localhost:11434/v1 или https://api.openai.com/v1"
                  value={llmBaseUrl}
                  disabled={!llmEnabled || llmLoading}
                  onChange={(e) => setLlmBaseUrl(e.target.value)}
                  fullWidth
                  sx={fieldSx}
                />
                <TextField
                  label="Model"
                  placeholder="qwen2.5:7b"
                  value={llmModel}
                  disabled={!llmEnabled || llmLoading}
                  onChange={(e) => setLlmModel(e.target.value)}
                  fullWidth
                  sx={fieldSx}
                />
                <TextField
                  label="API key"
                  type="password"
                  placeholder={llmHasApiKey ? 'API key сохранен' : 'API key'}
                  value={llmApiKey}
                  disabled={!llmEnabled || llmLoading}
                  onChange={(e) => setLlmApiKey(e.target.value)}
                  fullWidth
                  sx={fieldSx}
                />
                <TextField
                  label="Timeout, секунд"
                  type="number"
                  value={llmTimeout}
                  disabled={!llmEnabled || llmLoading}
                  inputProps={{ min: 1, max: 300 }}
                  onChange={(e) => setLlmTimeout(Number(e.target.value))}
                  fullWidth
                  sx={fieldSx}
                />
              </Stack>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                <Button
                  variant="contained"
                  onClick={handleSaveLLMSettings}
                  disabled={llmSaving || llmLoading || (llmEnabled && (!llmBaseUrl.trim() || !llmModel.trim()))}
                  sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 800, boxShadow: 'none' }}
                >
                  {llmSaving ? 'Сохранение...' : 'Сохранить'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleResetLLMSettings}
                  disabled={llmSaving || llmLoading}
                  sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 800, borderColor: border, color: text }}
                >
                  Удалить провайдер
                </Button>
              </Stack>
              {llmMessage && (
                <Typography sx={{ fontSize: 12, color: muted, mt: 1 }}>
                  {llmMessage}
                </Typography>
              )}
            </Box>

            <Box sx={cardSx}>
              <Typography sx={{ fontSize: 14, fontWeight: 800, mb: 1 }}>Условия проверки</Typography>
              <TextField
                value={checkRules}
                onChange={(e) => setCheckRules(e.target.value)}
                rows={5}
                multiline
                placeholder="Опишите условия проверки..."
                fullWidth
                sx={fieldSx}
              />
            </Box>
          </Stack>
        )}
      </Box>

      <Box
        component="nav"
        sx={{
          borderLeft: { xs: 'none', md: `1px solid ${border}` },
          borderTop: { xs: `1px solid ${border}`, md: 'none' },
          background: surface,
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        <NavItem active={section === 'about'} onClick={() => setSection('about')} text={text} border={border}>
          О пользователе
        </NavItem>
        <NavItem active={section === 'settings'} onClick={() => setSection('settings')} text={text} border={border}>
          Настройки
        </NavItem>
        {usingBackend && (
          <NavItem active={section === 'api-keys'} onClick={() => setSection('api-keys')} text={text} border={border}>
            API Keys
          </NavItem>
        )}
      </Box>
    </Box>
  );
};

const NavItem = ({ active, children, onClick, text, border }) => (
  <Button
    onClick={onClick}
    variant={active ? 'contained' : 'outlined'}
    sx={{
      justifyContent: 'flex-start',
      borderRadius: '8px',
      textTransform: 'none',
      fontWeight: 800,
      borderColor: border,
      color: active ? '#ffffff' : text,
      boxShadow: 'none',
    }}
  >
    {children}
  </Button>
);

function clampTimeout(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return 30;
  return Math.min(300, Math.max(1, Math.round(parsed)));
}

export default Profile;
